import {ChangeDetectorRef, Component, HostListener, inject, OnInit, ViewChild} from '@angular/core';
import {
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ExamStepperComponent } from '../../ui/stepper-container/stepper-container.component';
import { CdkStepper, CdkStepperModule } from '@angular/cdk/stepper';
import { CdkMenuModule } from '@angular/cdk/menu';
import { ConstructComponent } from './construct/construct.component';
import { AssignComponent } from './assign/assign.component';
import { SummaryComponent } from './summary/summary.component';
import { ExamService } from '../../data-access/exam.service';
import { Dialog } from '@angular/cdk/dialog';
import {
  DIALOG_ACTION_STYLE,
  ConfirmDialogComponent,
} from 'src/app/shared/ui/confirm-dialog/confirm-dialog.component';
import { Observable, of, switchMap } from 'rxjs';
import { ComponentCanDeactivate } from '../../utils/pending-changes.guard';

import { Course, Subsection } from 'src/app/admin/data-access/course.model';
import { TooltipDirective } from 'src/app/shared/ui/tooltip/tooltip.component';
import {ActivatedRoute, Router, RouterLink} from "@angular/router";
import {LinkComponent} from "./link/link.component";
import {MatSnackBar} from "@angular/material/snack-bar";
import {OutlineButtonComponent} from "../../../shared/ui/outline-button/outline-button.component";
import {FullButtonComponent} from "../../../shared/ui/full-button/full-button.component";
import { Timeframe } from '../../data-access/exam.model';
import {DatePipe} from "@angular/common";

interface LastTouched {
  who: string;
  when: Date
}

@Component({
    selector: 'app-exam-create',
    templateUrl: './create.component.html',
    styleUrls: ['./create.component.scss'],
  imports: [
    ExamStepperComponent,
    CdkStepperModule,
    ConstructComponent,
    AssignComponent,
    SummaryComponent,
    ReactiveFormsModule,
    CdkMenuModule,
    TooltipDirective,
    RouterLink,
    LinkComponent,
    OutlineButtonComponent,
    FullButtonComponent,
    DatePipe
  ]
})
export class CreateComponent implements ComponentCanDeactivate, OnInit {
  constructor(examService: ExamService, private _snackBar: MatSnackBar, private route: ActivatedRoute, private cdr: ChangeDetectorRef) {}
  router = inject(Router)
  examService = inject(ExamService);
  dialog = inject(Dialog);

  courses: Course[] = [];
  subsections: Subsection[] = [];

  saveClick = false
  isEdit = false
  isClone = false

  exam_id = -1

  whoLastTouchedThisExam: LastTouched | null = null;

  @ViewChild('stepper') stepper!: CdkStepper;
  @ViewChild('assignref') assignref: AssignComponent | null = null;

  formSteps = {
    'Build': 0,
    'Link': 1,
    'Assign': 2
  }

  /*
    NOTE: Form is structured on the basis of how it appears in the UI.
    Feel free to edit the form as the UI changes, as it will be easier to import
    each sections's appropriate data.
  */
  examForm = new FormGroup({
    construct: new FormGroup({
      exam_title: new FormControl(''),
      type: new FormControl('exam'),
      course_id: new FormControl(null),
      exam_id: new FormControl(null),
      attemptsMax: new FormControl<number>(1),
      late_start: new FormControl(false),
      late_finish: new FormControl(false),
      general: new FormGroup({
        parts: new FormArray([])
      })
    }),
    flows: new FormArray([]),
    assign: new FormGroup({
      graded: new FormControl(true),
      timeframes: new FormArray([]),
      lateStart: new FormControl(false),
      lateFinish: new FormControl(1),
    }),
    reports: new FormControl([])
  });

  get construct(): FormGroup {
    return this.examForm.get('construct') as FormGroup;
  }

  get flows(): FormArray {
    return this.examForm.get('flows') as FormArray;
  }

  get general(): FormGroup {
    return this.construct.get('general') as FormGroup;
  }

  get parts(): FormArray {
    return this.general.get('parts') as FormArray;
  }

  get assign(): FormGroup {
    return this.examForm.get('assign') as FormGroup
  }

  get timeframes(): FormArray {
    return this.assign.get('timeframes') as FormArray
  }

  getLastTouched() {
    if(this.exam_id !== -1) {
      this.examService.getExamLastTouchedInfo(this.exam_id).subscribe((response) => {
        if(response.success && response.result != null) {
          this.whoLastTouchedThisExam = {
            who: response.result.who,
            when: new Date(response.result.when)
          }
        }
      })
    }
  }

  ngOnInit() {
    this.route.queryParams.subscribe(params => {
      if(params['edit'] || params['clone']) {
        this.exam_id = params['exam_id']
        this.getLastTouched()
        this.isEdit = true

        this.examService.getExamDetailsType(params['exam_id']).subscribe((response) => {
          if(response.success && response.result != null) {
            const construct = this.examForm.get('construct') as FormGroup
            construct.get('type')?.setValue(response.result.type)
          }
        })

        this.examService.examDetails(params['exam_id'], params['clone']).subscribe((response) => {
          if(response.success) {
            // TODO use response.result to fill out form
            const construct = this.examForm.get('construct') as FormGroup
            const general = construct.get('general') as FormGroup
            construct.get('exam_title')?.setValue(response.result.exam_title)
            construct.get('late_start')?.setValue(response.result.late_start)
            construct.get('late_finish')?.setValue(Boolean(response.result.late_finish))
            construct.get('attemptsMax')?.setValue(response.result.attempts_max ?? 1)
            if(params['clone']) {
              this.isClone = true
              construct.get('exam_id')?.setValue(null)
            } else {
              construct.get('exam_id')?.setValue(response.result.exam_id)
            }
            construct.get('course_id')?.setValue(response.result.course_id)
            this.examForm.get('reports')?.setValue(response.result.reports)

            for (let flow of response.result.flows) {
              const flowForm = new FormGroup({
                examflow_id: new FormControl(flow.examflow_id),
                exam_id: new FormControl(flow.exam_id),
                exampart_previous: new FormControl(flow.exampart_previous),
                exampart_next: new FormControl(flow.exampart_next),
                eval_sequence: new FormControl(flow.eval_sequence),
                req_overallscore_min: new FormControl(flow.req_overallscore_min),
                req_overallscore_max: new FormControl(flow.req_overallscore_max),
                req_partscore_min: new FormControl(flow.req_partscore_min),
                req_partscore_max: new FormControl(flow.req_partscore_max),
                req_qpool_id: new FormControl(flow.req_qpool_id),
                req_qpool_correctness_min: new FormControl(flow.req_qpool_correctness_min),
                req_qpool_correctness_max: new FormControl(flow.req_qpool_correctness_max),
                req_q_id: new FormControl(flow.req_q_id),
                req_q_correctness: new FormControl(flow.req_q_correctness),
                req_q_correctness_range: new FormControl(flow.req_q_correctness_range),
                req_q_response_value: new FormControl(flow.req_q_response_value),
                req_q_response_formatted: new FormControl(flow.req_q_response_formatted),
                flow_noMin: new FormControl(false),
                flow_noMax: new FormControl(false),
                _original_unconditional_next_part: new FormControl(null),
              })
              this.flows.push(flowForm)
            }

            //general.get('parts')?.setValue(response.result.parts)
            for(let part of response.result.parts) {
              let partsForm = new FormGroup({
                part_name: new FormControl(part.part_name, Validators.required),
                part_instructions: new FormControl(part.part_instructions),
                part_duration_minutes: new FormControl(part.part_duration_minutes),
                unconditional_next_part: new FormControl(part.unconditional_next_part),
                expanded: new FormControl(false),
                entrypoint: new FormControl(part.entrypoint),
                // TODO for editing of an exam need to fill exam_id with the right id
                exam_id: new FormControl(part.exam_id),
                exampart_id: new FormControl(part.exampart_id),
                qpools: new FormArray([])
              });
              let partsFormQPools = partsForm.get('qpools') as FormArray
              for(let qpool of part.qpools) {
                let qpoolForm = new FormGroup({
                  examqpool_id: new FormControl(qpool.examqpool_id),
                  exampart_id: new FormControl(qpool.exampart_id),
                  qpool_position: new FormControl(qpool.qpool_position),
                  presentation_order: new FormControl(qpool.presentation_order),
                  questions_used: new FormControl(qpool.questions_used),
                  score_per_question: new FormControl(qpool.score_per_question),
                  expanded: new FormControl(true),
                  questions: new FormArray([])
                })
                let qpoolFormQuestions = qpoolForm.get('questions') as FormArray
                for(let question of qpool.questions) {
                  let questionForm = new FormGroup({
                    examquestion_id: new FormControl(question.examquestion_id),
                    examqpool_id: new FormControl(question.examqpool_id),
                    question_position: new FormControl(question.question_position),
                    problemtemplate_id: new FormControl(question.problemtemplate_id),
                    source: new FormControl(question.source),
                    source_id: new FormControl(question.source_id)
                  })
                  qpoolFormQuestions.push(questionForm)
                }
                partsFormQPools.push(qpoolForm)
              }
              this.parts.push(partsForm)
            }
            if(response.result.timeframes != null) {
              for(let time of response.result.timeframes) {
                const timeForm = new FormGroup({
                  subsection_id: new FormControl(time.subsection_id, Validators.required),
                  first_start: new FormControl(time.first_start),
                  last_start: new FormControl(time.last_start),
                  finish_by: new FormControl(time.finish_by)
                })
                this.timeframes.push(timeForm)
              }
            }
            if(this.assignref != null) {
              this.assignref.listSubsections(response.result.course_id)
              if(this.timeframes.getRawValue().length < 1) {
                this.assignref.addTimeframe()
              }
            }
          }
        })
      }
    })
  }

  @HostListener('window:beforeunload')
  unloadHandler() {
    if (this.examForm.dirty) {
      return false;
    }
    return true;
  }

  canDeactivate(): Observable<boolean> {
    if (this.examForm.dirty && !this.saveClick) {
      const dialogRef = this.dialog.open<string>(ConfirmDialogComponent, {
        data: {
          title: 'Unsaved Changes',
          message: 'You have some unsaved changes. Do you wish to save?',
          button_actions: [
            {
              label: 'Exit without Saving',
              style: DIALOG_ACTION_STYLE.DANGER,
              action: () => 'Discard',
            },
            {
              label: 'Save and Exit',
              style: DIALOG_ACTION_STYLE.PRIMARY,
              action: () => 'Confirm',
            },
            {
              label: 'Cancel',
              style: DIALOG_ACTION_STYLE.DEFAULT,
              action: () => 'Cancel',
            },
          ],
        },
      });

      return dialogRef.closed.pipe(
        switchMap((result) => {
          if (result === 'Confirm') {
            // return this.examService.saveExam();
          }
          if (result === 'Discard') {
            return of(true);
          }
          return of(false);
        })
      );
    }
    return of(true);
  }

  requestDelete() {
    const dialogRef = this.dialog.open<string>(ConfirmDialogComponent, {
      data: {
        title: 'Delete Exam',
        message: 'Are you sure you want to delete this exam?',
        button_actions: [
          {
            label: 'Delete',
            style: DIALOG_ACTION_STYLE.DANGER,
            action: () => 'Delete',
          },
          {
            label: 'Cancel',
            style: DIALOG_ACTION_STYLE.DEFAULT,
            action: () => 'Cancel',
          },
        ],
      },
    });

    dialogRef.closed.subscribe((result) => {
      if (result === 'Delete') {
        // this.examService.deleteExam();
      }
    });
  }

  saveExit() {
    this.saveClick = true
    this.examService.saveExam(JSON.stringify(this.examForm.getRawValue())).subscribe((response) => {
      if(response.success) {
        // TODO maybe use refer in url so that I don't need to do this type of check
        this.router.navigate(['/dashboard/exam/list'])
        this._snackBar.open('Exam successfully saved', 'Dismiss', {duration: 3000})
      } else {
        this._snackBar.open(response.message, 'Dismiss', {panelClass: ['error-snackbar']})
      }
    })
  }

  save() {
    this.saveClick = true
    this.examService.saveExam(JSON.stringify(this.examForm.getRawValue())).subscribe((response) => {
      if(response.success) {
        this._snackBar.open('Exam successfully saved', 'Dismiss', {duration: 3000})
        this.getLastTouched()
      } else {
        this._snackBar.open(response.message, 'Dismiss', {panelClass: ['error-snackbar']})
      }
    })
  }

  formValidation(exit: boolean) {
    if (this.examForm.valid) {
      this.transformConditionalFlowIds();
      // With new save button we do not convert to UTC here, we do it in exam.service.ts
      //this.convertTimeframesDateToUTC();
      if(exit)
        this.saveExit();
      else
        this.save()
    }
    else {
      this.examForm.markAllAsTouched();
      this.examForm.updateValueAndValidity();
      this.goToFormStepWithError();
      return;
    }
  }

  goToFormStepWithError() {
    if (this.assign.invalid || this.construct.get('course_id')?.invalid || this.construct.get('exam_title')?.invalid) {
      this.stepper.selectedIndex = this.formSteps.Build;
    }
  }

  convertTimeframesDateToUTC() {
    this.timeframes.controls.forEach(item => {
      const firstStart = item.get('first_start')?.value;
      const lastStart = item.get('last_start')?.value;
      const finishBy = item.get('finish_by')?.value;

      if (firstStart) {
        item.get('first_start')?.setValue(new Date(firstStart).toISOString());
      }
      if (lastStart) {
        item.get('last_start')?.setValue(new Date(lastStart).toISOString());
      }
      if (finishBy) {
        item.get('finish_by')?.setValue(new Date(finishBy).toISOString());
      }
    });
  }

  //backend expects conditional flow parts ids to be a negative number when creating an exam
  transformConditionalFlowIds(){
    if (!this.isEdit) {
      this.flows.controls.forEach(group => {
        const examNextPart = group.get('exampart_next')?.value;
        const examPreviousPart = group.get('exampart_previous')?.value;
        if (examNextPart > 0)
          group.get('exampart_next')?.setValue(examNextPart * -1, { emitEvent: false });
        if (examPreviousPart > 0)
          group.get('exampart_previous')?.setValue(examPreviousPart * -1, { emitEvent: false });
      });
    }
  }

  cancelClick() {
    if(this.isEdit || this.isClone)
      this.router.navigate(['/dashboard/exam/list'])
    else
      this.router.navigate(['/dashboard']);
  }
}
